import {
  ActionIcon,
  Button,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Skeleton,
  Table,
  Text,
  Tooltip as MantineTooltip,
  UnstyledButton,
} from "@mantine/core";
import type { TicketZeitraum, Vorgangstyp } from "@msc/shared";
import type { TFunction } from "i18next";
import { useEffect, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { CHART_COLORS } from "../../theme.js";
import { hasRole, useCurrentUser } from "../../hooks/useAuth.js";
import { formatIsoDate, toLocalDateString } from "../../lib/date.js";
import { zeigePlan } from "../../lib/planAnzeige.js";
import { wachstumProzent } from "../../lib/wachstum.js";
import { useEmployees } from "../../hooks/useEmployees.js";
import {
  useSetEngagementTarget,
  useUpdateEngagement,
} from "../../hooks/useEngagements.js";
import {
  useEngagementKpis,
  useLeadEngagements,
  useLeadMonthlyKpis,
  useLeadsOverview,
  useZielrunde,
  type LeadEngagementRow,
  type LeadOverviewRow,
} from "../../hooks/useKpi.js";
import { useKpiTicketLeads } from "../../hooks/useKpiTickets.js";
import { BegruendungModal } from "./BegruendungModal.js";
import { sortiereLeads, type LeadSpalte } from "./leadsSortierung.js";
import { ZahlKnopf } from "./tickets/ReaktionTabelle.js";
import { TicketAuswahlModal, type TicketAuswahl } from "./tickets/TicketAuswahlModal.js";
import { LeadTicketBloecke } from "./tickets/LeadTicketBloecke.js";
import { TicketBlockKopf } from "./tickets/TicketBlockKopf.js";

/**
 * Leads-Tab des Bereichs-Cockpits: Überblick aller Leads (Zeile wählt),
 * darunter das Lead-Cockpit — ALLE Beauftragungen des Leads quer über die
 * Kunden mit Inline-Pflege (Stellvertreter, Umsetzer-Staffing, Ziel €⇄%
 * auf Vorjahresbasis) ohne Absprung ins Kundendetail — plus Monats-Charts,
 * Budget-Burndown und Drill-down: Klick auf eine Beauftragung zeigt dieselben
 * KPIs (Vorjahr/Ziel/Ist/DB%/Prognose) nur für diese Kunden-Beauftragung.
 * Leads pflegen ihre eigenen Beauftragungen selbst (Server-Rechte
 * entsprechend erweitert). „Mein Portfolio" nutzt LeadDetail direkt — dort
 * ohne Lead-Überblick, fest auf die eigene Person.
 */

const MONTH_SHORT = [
  "Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
  "Jul", "Aug", "Sep", "Okt", "Nov", "Dez",
];

const fmtEur = (v: number | null | undefined) =>
  v == null ? "–" : `${Math.round(v).toLocaleString("de-DE")} €`;
const fmtPct = (v: number | null | undefined) =>
  v == null ? "–" : `${v.toFixed(1)} %`;

/**
 * Der Plan wird NUR gezeigt, wenn die Runde eingefroren ist UND er vom
 * heutigen Ziel abweicht — dieselbe Regel wie im Ziele-Reiter (siehe
 * `lib/planAnzeige.ts`: ein reiner Wertevergleich verwechselt „nie
 * eingefroren" mit „eingefroren, aber nichts geplant"). Wo abgewichen wurde,
 * ist die Begründung als Tooltip erreichbar.
 */
function PlanHinweisZeile({
  t,
  zielrundeStatus,
  current,
  plan,
  reason,
}: {
  t: TFunction;
  zielrundeStatus: "offen" | "eingefroren" | undefined;
  current: number | null | undefined;
  plan: number | null | undefined;
  reason: string | null | undefined;
}) {
  if (!zeigePlan(zielrundeStatus, plan, current)) return null;
  return (
    <Text
      size="xs"
      c="dimmed"
      title={
        reason
          ? t("pages.kpi.zielrunde.planBegruendungTitle", { begruendung: reason })
          : undefined
      }
    >
      {t("pages.kpi.zielrunde.plan", { betrag: fmtEur(plan) })}
    </Text>
  );
}

export function LeadsPanel({
  segmentId,
  year,
}: {
  segmentId: string;
  year: number;
}) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const { data: overview } = useLeadsOverview(segmentId, year);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [sortierung, setSortierung] = useState<{ spalte: LeadSpalte; richtung: "asc" | "desc" }>({ spalte: "fullName", richtung: "asc" });
  const [ticketZeitraum, setTicketZeitraum] = useState<TicketZeitraum | null>(null);
  const [ticketTyp, setTicketTyp] = useState<Vorgangstyp | null>(null);
  const [auswahl, setAuswahl] = useState<TicketAuswahl | null>(null);
  const { data: tickets } = useKpiTicketLeads(segmentId, ticketZeitraum, ticketTyp);
  const ticketsJeLead = new Map((tickets?.leads ?? []).map((l) => [l.employeeId, l]));

  const wert = (l: LeadOverviewRow): number | string | null => {
    const spalte = sortierung.spalte;
    switch (spalte) {
      case "offen":
        return ticketsJeLead.get(l.employeeId)?.offen ?? null;
      case "netto":
        return ticketsJeLead.get(l.employeeId)?.netto ?? null;
      default:
        return l[spalte];
    }
  };
  const sortiert = sortiereLeads(overview ?? [], wert, sortierung.richtung);
  const sortiereNach = (spalte: LeadSpalte) =>
    setSortierung((s) => ({ spalte, richtung: s.spalte === spalte && s.richtung === "asc" ? "desc" : "asc" }));
  const kopf = (spalte: LeadSpalte, label: string, hinweis?: string) => {
    const knopf = (
      <UnstyledButton fz="sm" fw={700} onClick={() => sortiereNach(spalte)}>
        {label}
        {sortierung.spalte === spalte ? (sortierung.richtung === "asc" ? " ▲" : " ▼") : ""}
      </UnstyledButton>
    );
    return (
      <Table.Th aria-sort={sortierung.spalte === spalte ? (sortierung.richtung === "asc" ? "ascending" : "descending") : "none"}>
        {hinweis ? (
          <MantineTooltip label={hinweis} multiline w={260} withArrow>
            {knopf}
          </MantineTooltip>
        ) : (
          knopf
        )}
      </Table.Th>
    );
  };
  const zeigeLeadTickets = (l: LeadOverviewRow, art: "offen" | "netto") => {
    if (!tickets) return;
    // Stichtag „heute": morgen als ausgeschlossenes Ende. Nicht `zeitraum.bis` — bei „Vormonat"
    // endet der Zeitraum früher. Ein Tag Versatz durch die Browser-Zeitzone schadet nicht,
    // der Server kappt einen späteren Stichtag auf heute (C5).
    const morgen = new Date();
    morgen.setDate(morgen.getDate() + 1);
    const heuteBis = toLocalDateString(morgen);
    setAuswahl(
      art === "offen"
        ? { titel: t("pages.kpi.tickets.leads.auswahlOffen", { lead: l.fullName }), abschnitte: [{ titel: t("pages.kpi.tickets.offenHeute"), abfrage: { quelle: "offen", von: tickets.zeitraum.von, bis: heuteBis, vorgangstyp: ticketTyp, leadId: l.employeeId } }] }
        : {
            titel: t("pages.kpi.tickets.leads.auswahlNetto", { lead: l.fullName }),
            abschnitte: [
              { titel: t("pages.kpi.tickets.erstellt"), abfrage: { quelle: "erstellt", ...tickets.zeitraum, vorgangstyp: ticketTyp, leadId: l.employeeId } },
              { titel: t("pages.kpi.tickets.geloest"), abfrage: { quelle: "geloest", ...tickets.zeitraum, vorgangstyp: ticketTyp, leadId: l.employeeId } },
            ],
          },
    );
  };

  // Vorauswahl: der eingeloggte Nutzer, wenn er selbst Lead ist — sonst erster.
  useEffect(() => {
    if (leadId || !overview || overview.length === 0) return;
    const self = overview.find((l) => l.employeeId === user?.employeeId);
    setLeadId((self ?? overview[0]!).employeeId);
  }, [overview, leadId, user?.employeeId]);

  // Summenzeile: €-Spalten addieren; DB % und Prognose-Wachstum GEWICHTET
  // rechnen (marginCumPct·ytd rekonstruiert ytd − Kosten; Prozente addieren
  // wäre falsch).
  const rows = overview ?? [];
  // Nur Leads der Übersicht zählen; solange die Ticketzahlen fehlen, steht „–“ statt 0.
  const ticketSumme = tickets
    ? rows.reduce(
        (a, r) => {
          const z = ticketsJeLead.get(r.employeeId);
          return { offen: a.offen + (z?.offen ?? 0), netto: a.netto + (z?.netto ?? 0) };
        },
        { offen: 0, netto: 0 },
      )
    : null;
  const sumPrev = rows.reduce((a, l) => a + l.prevYearRevenueEur, 0);
  const sumTarget = rows.reduce((a, l) => a + (l.targetRevenueEur ?? 0), 0);
  const sumYtd = rows.reduce((a, l) => a + l.actualYtdEur, 0);
  const sumContribution = rows.reduce(
    (a, l) =>
      a + (l.marginCumPct != null ? (l.marginCumPct / 100) * l.actualYtdEur : 0),
    0,
  );
  const sumMarginPct = sumYtd > 0 ? (sumContribution / sumYtd) * 100 : null;
  const sumForecast = rows.reduce((a, l) => a + (l.forecastEur ?? 0), 0);
  const sumForecastGrowthPct =
    sumPrev > 0 ? (sumForecast / sumPrev - 1) * 100 : null;

  return (
    <>
      <Paper p="md" withBorder mb="md">
        <Text fw={600} size="sm" mb="xs">
          {t("pages.kpi.leads.overviewTitle")}
        </Text>
        <TicketBlockKopf
          titel={t("pages.kpi.tickets.leads.kopf")}
          hinweis={t("pages.kpi.tickets.leads.kopfHinweis")}
          spiegel={tickets?.spiegel}
          vorgangstyp={ticketTyp}
          onVorgangstyp={setTicketTyp}
          zeitraum={ticketZeitraum}
          onZeitraum={setTicketZeitraum}
          zeitraumVorgabe="letzte_12_monate"
        />
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              {kopf("fullName", t("pages.kpi.leads.colLead"))}
              {kopf("prevYearRevenueEur", t("pages.kpi.leads.colPrevYear"))}
              {kopf("targetRevenueEur", t("pages.kpi.leads.colTarget"))}
              {kopf("actualYtdEur", t("pages.kpi.leads.colYtd"))}
              {kopf("marginCumPct", t("pages.kpi.leads.colMargin"))}
              {kopf("forecastEur", t("pages.kpi.leads.colForecast"))}
              {kopf("forecastGrowthPct", t("pages.kpi.leads.colForecastGrowth"))}
              {kopf("offen", t("pages.kpi.tickets.leads.colOffen"), t("pages.kpi.tickets.leads.hinweisOffen"))}
              {kopf("netto", t("pages.kpi.tickets.leads.colNetto"), t("pages.kpi.tickets.leads.hinweisNetto"))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortiert.map((l) => (
              <Table.Tr
                key={l.employeeId}
                onClick={() => setLeadId(l.employeeId)}
                style={{ cursor: "pointer" }}
                bg={l.employeeId === leadId ? "var(--mantine-color-blue-0)" : undefined}
              >
                <Table.Td>
                  <Text size="sm" fw={l.employeeId === leadId ? 700 : 500}>
                    {l.fullName}
                  </Text>
                </Table.Td>
                <Table.Td>{fmtEur(l.prevYearRevenueEur)}</Table.Td>
                <Table.Td>{fmtEur(l.targetRevenueEur)}</Table.Td>
                <Table.Td>{fmtEur(l.actualYtdEur)}</Table.Td>
                <Table.Td>{fmtPct(l.marginCumPct)}</Table.Td>
                <Table.Td>{fmtEur(l.forecastEur)}</Table.Td>
                <Table.Td
                  c={
                    l.forecastGrowthPct == null
                      ? undefined
                      : l.forecastGrowthPct >= 0
                        ? "green"
                        : "red"
                  }
                >
                  {fmtPct(l.forecastGrowthPct)}
                </Table.Td>
                {(() => {
                  const tz = ticketsJeLead.get(l.employeeId);
                  // Klick auf eine Zahl darf die Zeile nicht zusätzlich auswählen
                  const halt = (e: MouseEvent) => e.stopPropagation();
                  return (
                    <>
                      <Table.Td onClick={halt}>
                        {tz ? <ZahlKnopf wert={tz.offen} label={t("pages.kpi.tickets.leads.knopfOffen", { lead: l.fullName })} onClick={() => zeigeLeadTickets(l, "offen")} /> : "–"}
                      </Table.Td>
                      <Table.Td onClick={halt} c={tz ? (tz.netto > 0 ? "red" : tz.netto < 0 ? "green" : undefined) : undefined}>
                        {tz ? (
                          <UnstyledButton aria-label={t("pages.kpi.tickets.leads.knopfNetto", { lead: l.fullName, netto: tz.netto })} onClick={() => zeigeLeadTickets(l, "netto")} fz="sm" td="underline">
                            {tz.netto > 0 ? `+${tz.netto}` : tz.netto}
                          </UnstyledButton>
                        ) : "–"}
                      </Table.Td>
                    </>
                  );
                })()}
              </Table.Tr>
            ))}
            {rows.length > 1 && (
              <Table.Tr
                style={{
                  borderTop: "2px solid var(--mantine-color-gray-4)",
                }}
              >
                <Table.Td>
                  <Text size="sm" fw={700}>
                    {t("pages.kpi.leads.sumRow", { count: rows.length })}
                  </Text>
                </Table.Td>
                <Table.Td fw={700}>{fmtEur(sumPrev)}</Table.Td>
                <Table.Td fw={700}>{fmtEur(sumTarget)}</Table.Td>
                <Table.Td fw={700}>{fmtEur(sumYtd)}</Table.Td>
                <Table.Td fw={700}>{fmtPct(sumMarginPct)}</Table.Td>
                <Table.Td fw={700}>{fmtEur(sumForecast)}</Table.Td>
                <Table.Td
                  fw={700}
                  c={
                    sumForecastGrowthPct == null
                      ? undefined
                      : sumForecastGrowthPct >= 0
                        ? "green"
                        : "red"
                  }
                >
                  {fmtPct(sumForecastGrowthPct)}
                </Table.Td>
                <Table.Td fw={700}>{ticketSumme ? ticketSumme.offen : "–"}</Table.Td>
                <Table.Td fw={700} c={ticketSumme ? (ticketSumme.netto > 0 ? "red" : ticketSumme.netto < 0 ? "green" : undefined) : undefined}>
                  {ticketSumme ? (ticketSumme.netto > 0 ? `+${ticketSumme.netto}` : ticketSumme.netto) : "–"}
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
        <TicketAuswahlModal segmentId={segmentId} auswahl={auswahl} jiraBaseUrl={tickets?.jiraBaseUrl ?? null} onClose={() => setAuswahl(null)} />
      </Paper>

      {leadId && <LeadDetail segmentId={segmentId} year={year} leadId={leadId} />}
    </>
  );
}

/**
 * Lead-Cockpit unterhalb des Überblicks: Beauftragungs-Tabelle mit
 * Inline-Pflege + Drill-down, Monats-Chart, Budget-Burndown. Wird auch von
 * „Mein Portfolio" gerendert (dort fest auf den eingeloggten Lead).
 */
export function LeadDetail({
  segmentId,
  year,
  leadId,
}: {
  segmentId: string;
  year: number;
  leadId: string;
}) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const { data: engagements } = useLeadEngagements(segmentId, leadId, year);
  const { data: zielrunde } = useZielrunde(segmentId, year);
  const [transferTarget, setTransferTarget] = useState<LeadEngagementRow | null>(
    null,
  );
  const [aenderungTarget, setAenderungTarget] =
    useState<LeadEngagementRow | null>(null);
  // Drill-down: KPIs einer einzelnen Beauftragung.
  const [drilldownId, setDrilldownId] = useState<string | null>(null);
  // Die eine Auswahl: Umsatzkurve und Burndown lesen sie hier, die Kennzahlenleiste und
  // die Ticket-Blöcke unten ebenfalls.
  const { data: monthly } = useLeadMonthlyKpis(segmentId, leadId, year, drilldownId);
  useEffect(() => {
    setDrilldownId(null);
  }, [segmentId, leadId, year]);

  // Lead-Wechsel ist Segment-Führung — Button nur für BL/Stellv./GL/Admin.
  const canTransfer = hasRole(user, [
    "bereichsleiter",
    "stellvertreter",
    "geschaeftsleitung",
    "admin",
  ]);

  // Eingefroren = Zielrunde abgeschlossen. Ab dann ist die Inline-Pflege für
  // NIEMANDEN mehr offen — Änderungen laufen nur noch über den
  // Änderungsdialog der Segment-Führung mit Begründung (s. u.).
  const eingefroren = zielrunde?.status === "eingefroren";

  /**
   * Wer Stellvertreter und Umsetzer inline pflegen darf.
   *
   * **Genau die Regel, die der Server anwendet** (`updateEngagement`): die
   * Segment-Führung immer, der Service Lead nur an seinen EIGENEN
   * Beauftragungen. Vorher standen die Felder in jeder Zeile — auch in denen
   * fremder Leads. Der Server wies das mit 403 ab, die Person erfuhr es aber
   * erst beim Speichern (aufgefallen am 2026-08-20: Markus Wolle sah die
   * Eingabefelder an den Beauftragungen von Janis Meyer).
   *
   * Ein Feld anzubieten, das man nicht abschicken darf, ist kein Schutzfehler,
   * sondern ein Versprechen, das die Oberfläche nicht halten kann.
   *
   * **Bewusst OHNE `!eingefroren`:** Die Zielrunde friert nur die Ziele ein —
   * die Besetzung ändert sich das Jahr über (Urlaub, Krankheit), und
   * `updateEngagement` lässt Stellvertreter/Umsetzer unabhängig vom
   * Zielrunden-Status weiterhin zu. Eine Runde, die zusätzlich die Besetzung
   * sperrt, verhindert genau die Pflege, die während des Jahres nötig bleibt.
   */
  const darfBesetzungPflegen = canTransfer || leadId === user?.employeeId;

  /**
   * Wer das Zielfeld inline pflegen darf — zusätzlich zu `darfBesetzungPflegen`
   * nur, solange die Runde offen ist. Eingefroren sperrt IMMER, auch die
   * Segment-Führung — Änderungen an eingefrorenen Zielen laufen ab dann nur
   * noch über den Änderungsdialog mit Begründung, nicht mehr über die
   * formlose Inline-Pflege (`setEngagementTarget` weist das ohne Begründung
   * server­seitig mit 400 `BEGRUENDUNG_FEHLT` zurück).
   */
  const darfZielPflegen = darfBesetzungPflegen && !eingefroren;

  const chartData = (monthly?.months ?? []).map((m, i) => ({
    name: MONTH_SHORT[i],
    soll: m.target ?? undefined,
    ist: m.actual,
    vorjahr: m.prevYear,
  }));
  const burndownData = (monthly?.months ?? []).map((m, i) => ({
    name: MONTH_SHORT[i],
    rest: m.budgetRemainingEur ?? undefined,
    sollLaufzeit: m.budgetSollLaufzeitEur,
    sollZiel: m.budgetSollZielEur ?? undefined,
    prognose: m.budgetPrognoseEur ?? undefined,
  }));
  const gewaehlt = (engagements ?? []).find((x) => x.id === drilldownId);
  const beauftragung = gewaehlt ? { id: gewaehlt.id, name: `${gewaehlt.customerName} – ${gewaehlt.name}` } : null;

  return (
    <>
      <Paper p="md" withBorder mb="md">
        <Group justify="space-between" mb="xs">
          <div>
            <Text fw={600} size="sm">
              {t("pages.kpi.leads.engagementsTitle")}
            </Text>
            <Text size="xs" c="dimmed">
              {t("pages.kpi.leads.drilldownHint")}
            </Text>
          </div>
          <Text size="sm" c="dimmed">
            {t("pages.kpi.leads.budgetLive", {
              value: fmtEur(monthly?.budgetLeftLiveEur ?? null),
            })}
          </Text>
        </Group>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("pages.kpi.leads.colCustomer")}</Table.Th>
              <Table.Th>{t("pages.kpi.leads.colDeputy")}</Table.Th>
              <Table.Th>{t("pages.kpi.leads.colImplementers")}</Table.Th>
              <Table.Th>{t("pages.kpi.leads.colEngPrevYear")}</Table.Th>
              <Table.Th>{t("pages.kpi.leads.colEngTarget")}</Table.Th>
              <Table.Th>{t("pages.kpi.leads.colEngMarginCum")}</Table.Th>
              {canTransfer && <Table.Th style={{ width: 60 }} />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(engagements ?? []).map((e) => (
              <LeadEngagementTableRow
                key={e.id}
                engagement={e}
                year={year}
                zielrundeStatus={zielrunde?.status}
                eingefroren={eingefroren}
                canTransfer={canTransfer}
                darfBesetzungPflegen={darfBesetzungPflegen}
                darfZielPflegen={darfZielPflegen}
                onTransfer={() => setTransferTarget(e)}
                onOpenAenderung={() => setAenderungTarget(e)}
                selected={e.id === drilldownId}
                onSelect={() =>
                  setDrilldownId((prev) => (prev === e.id ? null : e.id))
                }
              />
            ))}
            {(engagements ?? []).length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" size="sm" ta="center" py="sm">
                    {t("pages.kpi.leads.noEngagements")}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {drilldownId && (
        <EngagementKpiPanel
          engagementId={drilldownId}
          name={beauftragung?.name ?? ""}
          year={year}
          onClose={() => setDrilldownId(null)}
        />
      )}

      <Paper p="md" withBorder mb="md">
        <Text fw={600} size="sm" mb="xs">
          {t("pages.kpi.leads.revenueChart")}
        </Text>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={(v: number) => `${Math.round(v / 1000)}k`} />
            <Tooltip formatter={(v) => fmtEur(Number(v))} />
            <Legend />
            <Bar dataKey="ist" name={t("pages.kpi.bereich.seriesActual")} fill={CHART_COLORS.ist} />
            <Bar dataKey="vorjahr" name={t("pages.kpi.bereich.seriesPrevYear")} fill={CHART_COLORS.vorjahr} />
            <Line
              dataKey="soll"
              name={t("pages.kpi.bereich.seriesTarget")}
              stroke={CHART_COLORS.soll}
              strokeWidth={2}
              dot={false}
              type="monotone"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Paper>

      <Paper p="md" withBorder mb="md">
        <Text fw={600} size="sm">
          {t("pages.kpi.leads.burndownChart", {
            start: fmtEur(monthly?.budgetStartEur ?? 0),
          })}
        </Text>
        <Text size="xs" c="dimmed" mb="xs">
          {t("pages.kpi.leads.burndownHinweis")}
        </Text>
        {monthly?.budgetLeerAm && (
          <Text size="xs" fw={600} mb="xs">
            {t("pages.kpi.leads.burndownLeerAm", { datum: formatIsoDate(monthly.budgetLeerAm) })}
          </Text>
        )}
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={burndownData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={(v: number) => `${Math.round(v / 1000)}k`} />
            <Tooltip formatter={(v) => fmtEur(Number(v))} />
            <Legend />
            <ReferenceLine y={0} stroke="var(--mantine-color-gray-5)" />
            <Line
              dataKey="rest"
              name={t("pages.kpi.leads.seriesRemaining")}
              stroke={CHART_COLORS.ist}
              strokeWidth={2}
              type="monotone"
            />
            <Line
              dataKey="sollLaufzeit"
              name={t("pages.kpi.leads.seriesSollLaufzeit")}
              stroke={CHART_COLORS.vorjahr}
              strokeDasharray="5 5"
              dot={false}
              type="monotone"
            />
            <Line
              dataKey="sollZiel"
              name={t("pages.kpi.leads.seriesSollZiel")}
              stroke={CHART_COLORS.soll}
              strokeDasharray="2 4"
              dot={false}
              type="monotone"
            />
            <Line
              dataKey="prognose"
              name={t("pages.kpi.leads.seriesPrognose")}
              stroke={CHART_COLORS.ist}
              strokeWidth={1}
              strokeDasharray="8 3"
              dot={false}
              type="monotone"
            />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      <LeadTicketBloecke
        segmentId={segmentId}
        year={year}
        leadId={leadId}
        beauftragung={beauftragung}
      />

      <TransferLeadModal
        target={transferTarget}
        onClose={() => setTransferTarget(null)}
      />
      <ZielAendernModal
        target={aenderungTarget}
        year={year}
        onClose={() => setAenderungTarget(null)}
      />
    </>
  );
}

/**
 * Drill-down-Block: dieselben Kennzahlen wie der Lead-Überblick, nur für
 * EINE Kunden-Beauftragung. Sein Kopf ist die eine Filterzeile des Lead-Detail;
 * Umsatzkurve, Burndown und Ticket-Blöcke folgen derselben Auswahl.
 */
function EngagementKpiPanel({
  engagementId,
  name,
  year,
  onClose,
}: {
  engagementId: string;
  name: string;
  year: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useEngagementKpis(engagementId, year);

  return (
    <Paper p="md" withBorder mb="md" bg="var(--mantine-color-blue-0)">
      <Group justify="space-between" mb="sm">
        <Text fw={600} size="sm">
          {t("pages.kpi.tickets.gefiltert", { name })}
        </Text>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onClose}
          aria-label={t("pages.kpi.tickets.filterAufheben", { name })}
        >
          ✕
        </ActionIcon>
      </Group>
      {isLoading ? (
        <Skeleton height={80} />
      ) : data ? (
        <SimpleGrid cols={{ base: 2, md: 6 }}>
            <EngagementStat
              label={t("pages.kpi.leads.colPrevYear")}
              value={fmtEur(data.prevYearRevenueEur)}
            />
            <EngagementStat
              label={t("pages.kpi.leads.colTarget")}
              value={fmtEur(data.annualTargetEur)}
            />
            <EngagementStat
              label={t("pages.kpi.leads.colYtd")}
              value={fmtEur(data.actualYtdEur)}
            />
            <EngagementStat
              label={t("pages.kpi.leads.colMargin")}
              value={fmtPct(data.marginCumPct)}
            />
            <EngagementStat
              label={t("pages.kpi.leads.colForecast")}
              value={fmtEur(data.forecastEur)}
            />
            <EngagementStat
              label={t("pages.kpi.leads.colForecastGrowth")}
              value={fmtPct(data.forecastGrowthPct)}
              color={
                data.forecastGrowthPct == null
                  ? undefined
                  : data.forecastGrowthPct >= 0
                    ? "green"
                    : "red"
              }
            />
        </SimpleGrid>
      ) : null}
    </Paper>
  );
}

function EngagementStat({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text fw={700} c={color}>
        {value}
      </Text>
    </div>
  );
}

/** Falsch zugeordnete Beauftragung direkt an einen neuen Lead übertragen. */
function TransferLeadModal({
  target,
  onClose,
}: {
  target: LeadEngagementRow | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: allEmployees } = useEmployees();
  const updateEngagement = useUpdateEngagement(target?.customerId ?? "");
  const [newLead, setNewLead] = useState<string | null>(null);

  return (
    <Modal
      opened={target !== null}
      onClose={onClose}
      title={
        target
          ? t("pages.kpi.leads.transferTitle", {
              name: `${target.customerName} – ${target.name}`,
            })
          : ""
      }
    >
      <Select
        label={t("pages.kpi.leads.transferNewLead")}
        value={newLead}
        data={(allEmployees ?? []).map((e) => ({
          value: e.id,
          label: e.fullName,
        }))}
        searchable
        mb="md"
        onChange={setNewLead}
      />
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          disabled={!newLead}
          loading={updateEngagement.isPending}
          onClick={() =>
            target &&
            newLead &&
            updateEngagement.mutate(
              { engagementId: target.id, leadEmployeeId: newLead },
              {
                onSuccess: () => {
                  setNewLead(null);
                  onClose();
                },
              },
            )
          }
        >
          {t("pages.kpi.leads.transferConfirm")}
        </Button>
      </Group>
    </Modal>
  );
}

/**
 * Änderung eines eingefrorenen Ziels — nur für die Segment-Führung erreichbar
 * (der Klick, der dieses Modal öffnet, existiert nur für sie, s.
 * `LeadEngagementTableRow`), und nur mit Begründung.
 *
 * Zeigt BEIDE Zahlen, wie von der Zielrunde-Spec verlangt: was beim
 * Einfrieren geplant war (`planRevenueTargetEur` — unveränderlich, das ist
 * der Referenzpunkt) und was mit diesem Speichern NEU gesetzt wird. Der
 * Speichern-Knopf bleibt gesperrt, solange die Begründung kürzer als zehn
 * Zeichen ist — der Server lehnt unabhängig davon ohne Begründung mit 400
 * `BEGRUENDUNG_FEHLT` ab. Beides, weil die Oberfläche erklärt und der
 * Server schützt.
 */
function ZielAendernModal({
  target,
  year,
  onClose,
}: {
  target: LeadEngagementRow | null;
  year: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const setTarget = useSetEngagementTarget(target?.customerId ?? "");
  const [neuRevenue, setNeuRevenue] = useState<number | string>("");
  const [begruendung, setBegruendung] = useState("");

  // Bei jedem neu geöffneten Ziel mit dessen aktuellem Wert vorbelegen, die
  // Begründung leert sich — sie gehört zu GENAU dieser Änderung, nicht zur
  // vorherigen.
  useEffect(() => {
    setNeuRevenue(target?.revenueTargetEur ?? "");
    setBegruendung("");
  }, [target]);

  const speichern = () => {
    if (!target) return;
    const revenueTargetEur = neuRevenue === "" ? null : Number(neuRevenue);
    setTarget.mutate(
      {
        engagementId: target.id,
        year,
        revenueTargetEur,
        // Abgeleitet wie überall sonst (siehe `wachstumProzent`) — keine
        // eigene, potenziell veraltete Eingabe in diesem Dialog.
        growthTargetPct: wachstumProzent(
          revenueTargetEur,
          target.prevYearRevenueEur,
        ),
        begruendung,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <BegruendungModal
      opened={target !== null}
      onClose={onClose}
      title={
        target
          ? t("pages.kpi.zielrunde.aenderungTitel", {
              name: `${target.customerName} – ${target.name}`,
            })
          : ""
      }
      hinweis={t("pages.kpi.zielrunde.aenderungHinweis")}
      begruendung={begruendung}
      onBegruendungChange={setBegruendung}
      onSave={speichern}
      saving={setTarget.isPending}
      errorMessage={
        setTarget.isError
          ? setTarget.error instanceof Error
            ? setTarget.error.message
            : t("common.error")
          : null
      }
    >
      <Text size="sm" mb={4}>
        {t("pages.kpi.zielrunde.plan", {
          betrag: fmtEur(target?.planRevenueTargetEur),
        })}
      </Text>
      <NumberInput
        label={t("pages.kpi.leads.colEngTarget")}
        value={neuRevenue}
        onChange={setNeuRevenue}
        min={0}
        step={1000}
        suffix=" €"
        mb={4}
      />
      <Text size="xs" c="dimmed" mb="md">
        {t("pages.kpi.zielrunde.wirdGesetzt", {
          betrag: fmtEur(neuRevenue === "" ? null : Number(neuRevenue)),
        })}
      </Text>
    </BegruendungModal>
  );
}

/** Beauftragungs-Zeile mit Inline-Pflege (Stellv., Umsetzer, Ziel €⇄%). */
function LeadEngagementTableRow({
  engagement,
  year,
  zielrundeStatus,
  eingefroren,
  canTransfer,
  darfBesetzungPflegen,
  darfZielPflegen,
  onTransfer,
  onOpenAenderung,
  selected,
  onSelect,
}: {
  engagement: LeadEngagementRow;
  year: number;
  zielrundeStatus: "offen" | "eingefroren" | undefined;
  /** Zielrunde eingefroren — s. `eingefroren` in LeadDetail. */
  eingefroren: boolean;
  canTransfer: boolean;
  /** Stellvertreter/Umsetzer inline pflegen — siehe `darfBesetzungPflegen` in LeadDetail. */
  darfBesetzungPflegen: boolean;
  /** Zielfeld inline pflegen — siehe `darfZielPflegen` in LeadDetail. */
  darfZielPflegen: boolean;
  onTransfer: () => void;
  /** Öffnet den Änderungsdialog (nur eingefroren + Segment-Führung erreichbar). */
  onOpenAenderung: () => void;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const { data: allEmployees } = useEmployees();
  const updateEngagement = useUpdateEngagement(engagement.customerId);
  const setTarget = useSetEngagementTarget(engagement.customerId);

  // Der Änderungsdialog ist NUR für die Segment-Führung erreichbar, und nur
  // solange die Runde eingefroren ist — ein Service Lead sieht dieselbe
  // Zeile schreibgeschützt, ohne Klick-Einladung, weil er nach dem Einfrieren
  // gar keine Möglichkeit mehr hat, das Ziel selbst zu ändern.
  const darfAendern = eingefroren && canTransfer;

  const [revenueTarget, setRevenueTarget] = useState<number | string>(
    engagement.revenueTargetEur ?? "",
  );
  /**
   * Abgeleitet, nicht gespeichert — dieselbe Falle wie im Ziele-Reiter: Die
   * Umsatzsteigerung ist eine Sicht auf Zielumsatz und Vorjahresumsatz. Wird
   * der Vorjahreswert später ein anderer (nachgetragener Umsatz, ein Abgang,
   * der in den Bestand kommt), stimmt ein gespeichertes Prozent nicht mehr.
   */
  const [growthTarget, setGrowthTarget] = useState<number | string>(
    () =>
      wachstumProzent(
        engagement.revenueTargetEur,
        engagement.prevYearRevenueEur,
      ) ?? "",
  );

  const prevYear =
    engagement.prevYearRevenueEur > 0 ? engagement.prevYearRevenueEur : null;
  const employeeOptions = (allEmployees ?? []).map((e) => ({
    value: e.id,
    label: e.fullName,
  }));
  const nameVon = (id: string | null) =>
    id ? ((allEmployees ?? []).find((e) => e.id === id)?.fullName ?? "–") : "–";

  const saveTarget = (revenue: number | string, growth: number | string) => {
    setTarget.mutate({
      engagementId: engagement.id,
      year,
      revenueTargetEur: revenue === "" ? null : Number(revenue),
      growthTargetPct: growth === "" ? null : Number(growth),
    });
  };

  return (
    <Table.Tr bg={selected ? "var(--mantine-color-blue-0)" : undefined}>
      {/* Nur die Namenszelle togglet den Drill-down — die übrigen Zellen
          tragen Inline-Formulare und dürfen keinen Row-Click auslösen. */}
      <Table.Td
        onClick={onSelect}
        style={{ cursor: "pointer" }}
        title={t("pages.kpi.leads.drilldownHint")}
      >
        <Text size="sm" fw={selected ? 700 : 500} c="blue.8">
          {engagement.customerName}
        </Text>
        <Text size="xs" c="dimmed">
          {engagement.name}
        </Text>
      </Table.Td>
      <Table.Td>
        {darfBesetzungPflegen ? (
          <Select
            size="xs"
            placeholder={t("pages.customers.detail.engagementDeputy")}
            value={engagement.deputyEmployeeId}
            data={employeeOptions}
            searchable
            clearable
            onChange={(v) =>
              updateEngagement.mutate({
                engagementId: engagement.id,
                deputyEmployeeId: v,
              })
            }
            style={{ minWidth: 170 }}
          />
        ) : (
          <Text size="sm">{nameVon(engagement.deputyEmployeeId)}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {darfBesetzungPflegen ? (
          <MultiSelect
            size="xs"
            placeholder={t("pages.customers.detail.engagementImplementers")}
            value={engagement.implementers.map((i) => i.employeeId)}
            data={employeeOptions}
            searchable
            onChange={(ids) =>
              updateEngagement.mutate({
                engagementId: engagement.id,
                implementerIds: ids,
              })
            }
            style={{ minWidth: 200 }}
          />
        ) : (
          <Text size="sm">
            {engagement.implementers.map((i) => i.fullName).join(", ") || "–"}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="sm">{fmtEur(engagement.prevYearRevenueEur)}</Text>
      </Table.Td>
      <Table.Td>
        {!darfZielPflegen ? (
          <>
            <Text
              size="sm"
              c={darfAendern ? "blue.8" : undefined}
              style={darfAendern ? { cursor: "pointer" } : undefined}
              onClick={darfAendern ? onOpenAenderung : undefined}
              title={darfAendern ? t("pages.kpi.zielrunde.aenderungHinweis") : undefined}
            >
              {engagement.revenueTargetEur != null
                ? fmtEur(engagement.revenueTargetEur)
                : "–"}
            </Text>
            <PlanHinweisZeile
              t={t}
              zielrundeStatus={zielrundeStatus}
              current={engagement.revenueTargetEur}
              plan={engagement.planRevenueTargetEur}
              reason={engagement.planChangeReason}
            />
          </>
        ) : (
        <>
        <Group gap={4} wrap="nowrap">
          <NumberInput
            size="xs"
            value={revenueTarget}
            onChange={(v) => {
              setRevenueTarget(v);
              if (prevYear && v !== "") {
                setGrowthTarget(
                  Math.round((Number(v) / prevYear - 1) * 1000) / 10,
                );
              }
            }}
            onBlur={() => saveTarget(revenueTarget, growthTarget)}
            min={0}
            step={1000}
            suffix=" €"
            style={{ width: 130 }}
          />
          <NumberInput
            size="xs"
            value={growthTarget}
            onChange={(v) => {
              setGrowthTarget(v);
              if (prevYear && v !== "") {
                setRevenueTarget(Math.round(prevYear * (1 + Number(v) / 100)));
              }
            }}
            onBlur={() => saveTarget(revenueTarget, growthTarget)}
            min={-100}
            step={0.5}
            suffix=" %"
            disabled={!prevYear}
            style={{ width: 100 }}
          />
        </Group>
        <PlanHinweisZeile
          t={t}
          zielrundeStatus={zielrundeStatus}
          current={engagement.revenueTargetEur}
          plan={engagement.planRevenueTargetEur}
          reason={engagement.planChangeReason}
        />
        </>
        )}
      </Table.Td>
      <Table.Td>
        {/*
          Deckungsbeitrag kumuliert. Ohne Umsatz gibt es keine Quote — dann
          steht hier ein Gedankenstrich und keine 0 %, die behaupten wuerde,
          alles sei draufgegangen.
        */}
        <Text size="sm">{fmtPct(engagement.marginCumPct)}</Text>
      </Table.Td>
      {canTransfer && (
        <Table.Td>
          {/*
            Jira-verwaltete Beauftragungen sind nicht uebertragbar: Der Service
            Lead kommt aus der Kundenabteilung (F4), die API lehnt es mit 409
            ab. Der Knopf bleibt sichtbar, damit die Spalte nicht springt —
            aber gesperrt und mit dem Grund im Tooltip, statt den Weg erst nach
            der Auswahl abzubrechen.
          */}
          <Button
            size="compact-xs"
            variant="subtle"
            disabled={engagement.jiraManaged}
            title={
              engagement.jiraManaged
                ? t("pages.kpi.leads.transferJiraManaged")
                : t("pages.kpi.leads.transferButton")
            }
            onClick={onTransfer}
          >
            →
          </Button>
        </Table.Td>
      )}
    </Table.Tr>
  );
}
